import React, { useState, useEffect } from 'react';
import { Modal, Button, Label, TextInput, Select, Checkbox, Alert } from 'flowbite-react';
import { HiMail } from 'react-icons/hi';
import type { PersonEmailAssociation } from '../../types/api';

interface EditPersonEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  email?: PersonEmailAssociation | null;  // null for new email
  emailTypes?: Array<{ value: number; label: string }>;  // Available email types from descriptors
  onSave: (emailData: Partial<PersonEmailAssociation>) => Promise<void>;
}

export const EditPersonEmailModal: React.FC<EditPersonEmailModalProps> = ({
  isOpen,
  onClose,
  email,
  emailTypes = [],
  onSave
}) => {
  const [formData, setFormData] = useState({
    emailAddress: '',
    emailTypeDescriptorId: null as number | null,
    primaryEmailIndicator: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (email) {
      setFormData({
        emailAddress: email.emailAddress || '',
        emailTypeDescriptorId: email.emailTypeDescriptorId || null,
        primaryEmailIndicator: email.primaryEmailIndicator || false
      });
    } else {
      // Reset for new email
      setFormData({
        emailAddress: '',
        emailTypeDescriptorId: null,
        primaryEmailIndicator: false
      });
    }
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.emailAddress)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save email');
    } finally {
      setLoading(false);
    }
  };

  const isEditMode = !!email;

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="md">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <HiMail className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">
            {isEditMode ? 'Edit Email Address' : 'Add Email Address'}
          </h3>
        </div>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="emailAddress" className="block mb-2 text-sm font-medium text-gray-700">
              Email Address <span className="text-red-500">*</span>
            </Label>
            <TextInput
              id="emailAddress"
              type="email"
              placeholder="user@example.com"
              value={formData.emailAddress}
              onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
              required
              disabled={loading}
            />
          </div>

          {emailTypes.length > 0 && (
            <div>
              <Label htmlFor="emailType" className="block mb-2 text-sm font-medium text-gray-700">
                Email Type
              </Label>
              <Select
                id="emailType"
                value={formData.emailTypeDescriptorId || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  emailTypeDescriptorId: e.target.value ? Number(e.target.value) : null
                })}
                disabled={loading}
              >
                <option value="">Select type...</option>
                {emailTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="checkbox-wrapper">
            <Checkbox
              id="primaryEmail"
              checked={formData.primaryEmailIndicator}
              onChange={(e) => setFormData({ ...formData, primaryEmailIndicator: e.target.checked })}
              disabled={loading}
            />
            <Label htmlFor="primaryEmail" className="text-sm font-medium text-gray-700">
              Set as primary email address
            </Label>
          </div>

          {formData.primaryEmailIndicator && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                Setting this as primary will automatically unset any existing primary email.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button color="gray" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Email'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};